import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, LogOut } from "lucide-react";
import { accountSections } from "./NavbarMenus";

// Design review (Oct 2026): four public links only — How it Works, For Creators,
// Score Your Campaigns (Score Your Ad + ROI calculator combined) and Pricing.
export const PUBLIC_LINKS = [
  { href: "/#how-it-works", label: "How it Works" },
  { href: "/creators", label: "For Creators" },
  { href: "/score-your-ad", label: "Score Your Campaigns" },
  { href: "/#pricing", label: "Pricing" },
];

const ACCOUNT_PREFIXES = ["/studio", "/gallery", "/brand-kit", "/campaigns", "/credits", "/addons", "/admin", "/profile", "/education", "/analytics", "/onboarding", "/trash", "/success"];
export const isAccountPath = (pathname) => ACCOUNT_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/** Single control to jump between the account area and the public site. */
export const SwitchAreaLink = ({ inAccount, onClick = () => {}, mobile = false }) => (
  <Link to={inAccount ? "/" : "/studio"} onClick={onClick} data-testid={inAccount ? "nav-back-to-site" : "nav-open-account"}
    className={mobile
      ? "flex items-center gap-2 px-4 py-3 rounded-xl text-white font-bold bg-white/[0.06] hover:bg-white/10 transition-colors"
      : "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-white bg-white/[0.07] hover:bg-white/[0.14] ring-1 ring-white/15 transition-colors whitespace-nowrap"}>
    {inAccount ? <><ArrowLeft className="w-3.5 h-3.5" /> Back to site</> : <>Open my account <ArrowRight className="w-3.5 h-3.5" /></>}
  </Link>
);

export const MobileMenu = ({ open, user, inAccount, onClose, logout = () => {} }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}
        className="fixed top-[64px] left-0 right-0 z-40 lg:hidden glass border-t border-white/10" data-testid="mobile-menu">
        <div className="px-4 py-4 max-h-[calc(100vh-64px)] overflow-y-auto space-y-1">
          {user && <SwitchAreaLink inAccount={inAccount} onClick={onClose} mobile />}
          {!inAccount && PUBLIC_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={onClose} className="block px-4 py-3 rounded-xl text-white/85 hover:bg-white/5 transition-colors">{l.label}</a>
          ))}
          {inAccount && accountSections(user).map((section) => (
            <div key={section.title} className="pt-2">
              <p className="px-4 pb-1 text-[11px] uppercase tracking-wider text-white/40">{section.title}</p>
              {section.links.map((l) => (
                <Link key={l.id} to={l.to} onClick={onClose} data-testid={`nav-${l.id}-link`}
                  className={`block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors whitespace-nowrap ${l.admin ? "text-cyan-300" : "text-purple-200"}`}>
                  {l.label}
                </Link>
              ))}
            </div>
          ))}
          {user && (
            <button type="button" onClick={() => { onClose(); logout(); }} data-testid="mobile-logout-btn"
              className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-red-200 hover:bg-red-500/10 transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

/** Scroll-aware chrome + mobile drawer state (locks body scroll while open). */
export function useNavbarChrome() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return { scrolled, mobileOpen, toggleMobile: () => setMobileOpen((o) => !o), closeMobile: () => setMobileOpen(false) };
}
